export type CurlTokenType = "command" | "flag" | "url" | "header" | "param-key" | "param-value" | "punctuation";

export interface CurlDisplayToken {
  type: CurlTokenType;
  text: string;
}

export interface CurlDisplayLine {
  continuation: boolean;
  tokens: CurlDisplayToken[];
}

export interface ClientCredentialsCurl {
  text: string;
  lines: CurlDisplayLine[];
}

export const maskClientSecret = (secret: string): string => {
  if (!secret) return "";
  return secret.length > 4 ? `${secret.slice(0, 4)}*****` : "*****";
};

const escapeShellSingleQuoted = (value: string): string => value.replace(/'/g, "'\\''");

const dataUrlencodeLine = (key: string, value: string, continued: boolean): CurlDisplayLine => ({
  continuation: true,
  tokens: [
    { type: "flag", text: "--data-urlencode" },
    { type: "punctuation", text: " '" },
    { type: "param-key", text: key },
    { type: "punctuation", text: "=" },
    { type: "param-value", text: value },
    { type: "punctuation", text: continued ? "' \\" : "'" },
  ],
});

export function buildClientCredentialsCurl(params: {
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  displayClientSecret?: string;
  scope?: string;
}): ClientCredentialsCurl {
  const scope = params.scope ?? "openid";
  const escapedTokenUrl = escapeShellSingleQuoted(params.tokenUrl);
  const displayClientSecret = params.displayClientSecret ?? params.clientSecret;

  const lines: CurlDisplayLine[] = [
    {
      continuation: false,
      tokens: [
        { type: "command", text: "curl" },
        { type: "punctuation", text: " " },
        { type: "flag", text: "--location" },
        { type: "punctuation", text: " '" },
        { type: "url", text: escapedTokenUrl },
        { type: "punctuation", text: "' \\" },
      ],
    },
    {
      continuation: true,
      tokens: [
        { type: "flag", text: "--header" },
        { type: "punctuation", text: " '" },
        { type: "header", text: "Content-Type: application/x-www-form-urlencoded" },
        { type: "punctuation", text: "' \\" },
      ],
    },
    dataUrlencodeLine("grant_type", "client_credentials", true),
    dataUrlencodeLine("client_id", params.clientId, true),
    dataUrlencodeLine("client_secret", displayClientSecret, true),
    dataUrlencodeLine("scope", scope, false),
  ];

  const text = [
    `curl --location '${escapedTokenUrl}' \\`,
    `--header 'Content-Type: application/x-www-form-urlencoded' \\`,
    `--data-urlencode 'grant_type=client_credentials' \\`,
    `--data-urlencode 'client_id=${params.clientId}' \\`,
    `--data-urlencode 'client_secret=${params.clientSecret}' \\`,
    `--data-urlencode 'scope=${scope}'`,
  ].join("\n");

  return { text, lines };
}
